/**
 * @file index.tsx
 *
 * @fileoverview Renders a normal select.
 */

import { FormItem } from "./../../01-forms/11-form-item";
import { FormLabel } from "./../../01-forms/11-form-item/01-form-label";
import { FormDescription } from "./../../01-forms/11-form-item/02-form-description";
import classNames from "classnames";
import { FocusEvent, forwardRef } from "react";
import { SelectItem } from "./../../../../models/forms";
import styles from "./select.module.scss";

/**
 *
 * @param {SelectItem} item
 *  Select item.
 * @param {String} depthLevelChar
 *  Character inicating depth level.
 * @returns {String}
 *  Item name preceded with depth level character.
 */
export const getSelectOptionText = (
  item: SelectItem,
  depthLevelChar = "    "
): String => `${depthLevelChar.repeat(item.depth ?? 0)} ${item.name}`;

export interface SelectProps {
  /**
   * The id for this select. It will be used for accessibility purposes.
   */
  id?: string;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * An array of selected items for the select.
   *
   * @default []
   */
  items: SelectItem[];
  /**
   * The value that is selected.
   */
  selectedValue?: string;
  /**
   * Non-selectable empty value. Only applies if there is no defaultValue (selectedValue).
   */
  emptyValue?: string;
  /**
   * The label for the select.
   */
  label?: string;
  /**
   * Description for this field.
   */
  description?: string;
  /**
   * If the select is a required element.
   */
  required?: boolean;
  /**
   * The name property.
   */
  name?: string;
  /**
   * The event to fire when something changes in the select.
   */
  onChange?(item: SelectItem): any;
  /**
   * The event to fire when the user clicks outside the select.
   */
  onBlur?(event: FocusEvent<HTMLElement>): void;
}

const ArrowIcon = () => {
  return (
    <svg
      viewBox="0 0 20 20"
      preserveAspectRatio="none"
      width={16}
      fill="transparent"
      stroke="#979797"
      strokeWidth="1.1px"
    >
      <path d="M1,6 L10,15 L19,6" />
    </svg>
  );
};

/**
 * Filters for the id that was selected.
 *
 * @param {SelectItem} items
 *   The whole value list.
 * @param {string} value
 *   The code that was passed as default value
 *
 * @return {string}
 *   The value thRelativee pass to the input as default value.
 */
const getValue = (items: SelectItem[], value?: string): string => {
  const filteredItem: SelectItem[] = items.filter((i) => i.code === value);
  if (filteredItem.length > 0) {
    return filteredItem[0].code;
  }
  return "";
};

/**
 * A regular select component that styles only the outside of the normal HTML5 select.
 * Its fully accessible and on mobile will use the native select implementation
 * for the device.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      items = [],
      label,
      description,
      error = false,
      required = false,
      onChange,
      onBlur,
      selectedValue,
      emptyValue,
      name,
    },
    ref
  ) => {
    // Get the actual value (because we have code and name in each option).
    const value = getValue(items, selectedValue);
    return (
      <FormItem>
        {label && (
          <FormLabel error={error} required={required} htmlFor={id}>
            {label}
          </FormLabel>
        )}
        <div className={classNames(styles.relative, "jobiqo-select__wrapper")}>
          <select
            className={styles.select}
            id={id}
            name={name}
            required={required}
            style={{
              borderColor: error
                ? `var(--color-danger)`
                : `var(--select-border-color)`,
            }}
            onChange={(e) => {
              onChange &&
                onChange(
                  items.filter((item) => item.code === e.target.value)[0]
                );
            }}
            onBlur={onBlur}
            value={value ? value : ""}
            ref={ref}
          >
            {emptyValue ? (
              <option value="" disabled={required}>
                {emptyValue}
              </option>
            ) : null}
            {items.map((item) => {
              return (
                <option key={item.code} value={item.code}>
                  {getSelectOptionText(item, "-")}
                </option>
              );
            })}
          </select>
          <ArrowIcon />
        </div>
        {description && <FormDescription>{description}</FormDescription>}
      </FormItem>
    );
  }
);
